import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import { ChevronLeft, ChevronRight, RotateCcw, Printer, CheckCircle2, AlertTriangle, ArrowRight, ShieldCheck, HelpCircle } from 'lucide-react';

export default function AdhdScreening() {
  const { lang, t, localizePath } = useLanguage();
  
  // SEO Metadata
  const seoTitle = lang === 'da'
    ? 'ASRS-v1.1 ADHD Screening Test | Nordisk Psykiatri'
    : 'ASRS-v1.1 ADHD Screening Test | Nordisk Psykiatri';
  const seoDesc = lang === 'da'
    ? 'Tag den officielle WHO ASRS-v1.1 selvtest for voksen-ADHD. En gratis, anonym og vejledende screening med øjeblikkelig scoring.'
    : 'Gör det officiella WHO ASRS-v1.1 självtestet för vuxen-ADHD. En gratis, anonym och vägledande screening med direkt resultat.';

  useDocumentMetadata(seoTitle, seoDesc, { lang });

  // State
  const [currentStep, setCurrentStep] = useState(0); // 0: Intro, 1: Part A (Q1-6), 2: Part B (Q7-18), 3: Results
  const [answers, setAnswers] = useState({}); // { questionId: value } (value: 0-4)
  const [showAllQuestions, setShowAllQuestions] = useState(false); // Toggle between step-by-step and full list

  // Options
  const options = lang === 'da' 
    ? [
        { label: 'Aldrig', value: 0 },
        { label: 'Sjældent', value: 1 },
        { label: 'Sommetider', value: 2 },
        { label: 'Ofte', value: 3 },
        { label: 'Meget ofte', value: 4 }
      ]
    : [
        { label: 'Never', value: 0 },
        { label: 'Rarely', value: 1 },
        { label: 'Sometimes', value: 2 },
        { label: 'Often', value: 3 },
        { label: 'Very often', value: 4 }
      ];

  // Questions
  const questions = [
    // PART A (1-6)
    {
      id: 1,
      part: 'A',
      text: {
        da: 'Hvor ofte har du besvær med at gøre den sidste del af en opgave færdig, efter at de sværeste detaljer er løst?',
        sv: 'Hur ofta har du svårt att avsluta de sista detaljerna i en uppgift eller ett projekt när de mest krävande delarna är klara?'
      },
      // Positive if Sommetider (2), Ofte (3), Meget ofte (4)
      isPositive: (val) => val >= 2
    },
    {
      id: 2,
      part: 'A',
      text: {
        da: 'Hvor ofte har du besvær med at organisere dig, når du skal udføre en opgave, der kræver planlægning?',
        sv: 'Hur ofta har du svårt att få ordning på saker och ting när du ska utföra en uppgift som kräver organisation?'
      },
      isPositive: (val) => val >= 2
    },
    {
      id: 3,
      part: 'A',
      text: {
        da: 'Hvor ofte har du problemer med at huske aftaler eller forpligtelser?',
        sv: 'Hur ofta har du svårt att komma ihåg avtalade möten eller åtaganden?'
      },
      isPositive: (val) => val >= 2
    },
    {
      id: 4,
      part: 'A',
      text: {
        da: 'Når du har en opgave, der kræver stor tankevirksomhed, hvor ofte undgår eller udsætter du så at komme i gang?',
        sv: 'När du har en uppgift som kräver mycket eftertanke, hur ofta undviker eller skjuter du upp att komma igång?'
      },
      // Positive if Ofte (3), Meget ofte (4)
      isPositive: (val) => val >= 3
    },
    {
      id: 5,
      part: 'A',
      text: {
        da: 'Hvor ofte sidder du og fipser med hænder eller fødder, eller sidder uroligt på stolen, når du skal sidde stille i længere tid?',
        sv: 'Hur ofta sitter du och pillar med händerna, rör på fötterna eller skruvar på dig när du måste sitta stilla en längre stund?'
      },
      isPositive: (val) => val >= 3
    },
    {
      id: 6,
      part: 'A',
      text: {
        da: 'Hvor ofte føler du dig rastløs eller overaktiv, som om du er drevet af en indre motor?',
        sv: 'Hur ofta känner du dig överaktiv och driven att göra saker, som om du gick på högvarv?'
      },
      isPositive: (val) => val >= 3
    },
    // PART B (7-18)
    {
      id: 7,
      part: 'B',
      text: {
        da: 'Hvor ofte begår du sjuskefejl, når du arbejder på en kedelig eller svær opgave?',
        sv: 'Hur ofta gör du slarvfel när du arbetar med en tråkig eller svår uppgift?'
      },
      isPositive: (val) => val >= 3
    },
    {
      id: 8,
      part: 'B',
      text: {
        da: 'Hvor ofte har du svært ved at holde opmærksomheden på en opgave, når du arbejder med noget, der er kedeligt eller ensformigt?',
        sv: 'Hur ofta har du svårt att hålla kvar uppmärksamheten när du arbetar med något tråkigt eller enformigt?'
      },
      isPositive: (val) => val >= 3
    },
    {
      id: 9,
      part: 'B',
      text: {
        da: 'Hvor ofte har du svært ved at koncentrere dig om, hvad folk siger til dig, selv når de taler direkte til dig?',
        sv: 'Hur ofta har du svårt att koncentrera dig på vad folk säger, även när de talar direkt till dig?'
      },
      isPositive: (val) => val >= 3
    },
    {
      id: 10,
      part: 'B',
      text: {
        da: 'Hvor ofte forlægger du ting, eller har svært ved at finde ting derhjemme eller på arbejdet?',
        sv: 'Hur ofta förlägger du saker, eller har svårt att hitta saker hemma eller på jobbet?'
      },
      isPositive: (val) => val >= 3
    },
    {
      id: 11,
      part: 'B',
      text: {
        da: 'Hvor ofte bliver du distraheret af lyde eller hændelser omkring dig?',
        sv: 'Hur ofta blir du distraherad av ljud eller händelser i din omgivning?'
      },
      isPositive: (val) => val >= 3
    },
    {
      id: 12,
      part: 'B',
      text: {
        da: 'Hvor ofte forlader du din plads i møder eller i andre situationer, hvor det forventes, at du bliver siddende?',
        sv: 'Hur ofta lämnar du din plats vid möten eller i andra situationer där det förväntas att du ska sitta kvar?'
      },
      isPositive: (val) => val >= 3
    },
    {
      id: 13,
      part: 'B',
      text: {
        da: 'Hvor ofte føler du dig rastløs eller urolig indeni?',
        sv: 'Hur ofta känner du dig rastlös eller har svårt att vara stilla?'
      },
      // Positive if Sommetider (2), Ofte (3), Meget ofte (4)
      isPositive: (val) => val >= 2
    },
    {
      id: 14,
      part: 'B',
      text: {
        da: 'Hvor ofte har du svært ved at slappe af og koble af, når du har fri?',
        sv: 'Hur ofta har du svårt att koppla av och varva ner när du har ledig tid?'
      },
      isPositive: (val) => val >= 2
    },
    {
      id: 15,
      part: 'B',
      text: {
        da: 'Hvor ofte tager du dig selv i at tale for meget i sociale sammenhænge?',
        sv: 'Hur ofta kommer du på dig själv med att prata för mycket i sociala sammanhang?'
      },
      isPositive: (val) => val >= 3
    },
    {
      id: 16,
      part: 'B',
      text: {
        da: 'Når du taler med andre, hvor ofte oplever du så at fuldføre deres sætninger, før de selv kan nå at gøre dem færdige?',
        sv: 'När du pratar med andra, hur ofta upplever du att du avslutar deras meningar innan de själva hinner göra det?'
      },
      isPositive: (val) => val >= 3
    },
    {
      id: 17,
      part: 'B',
      text: {
        da: 'Hvor ofte har du svært ved at vente på, at det bliver din tur i situationer, hvor der er kø eller ventetid?',
        sv: 'Hur ofta har du svårt att vänta på din tur i situationer där det är kö eller väntetid?'
      },
      isPositive: (val) => val >= 3
    },
    {
      id: 18,
      part: 'B',
      text: {
        da: 'Hvor ofte afbryder du andre, når de er i gang med noget?',
        sv: 'Hur ofta avbryter du andra när de är upptagna med något?'
      },
      isPositive: (val) => val >= 3
    }
  ];

  const partAQuestions = questions.filter(q => q.part === 'A');
  const partBQuestions = questions.filter(q => q.part === 'B');

  // Handle Answer Selection
  const handleSelect = (qId, val) => {
    setAnswers(prev => ({
      ...prev,
      [qId]: val
    }));
  };

  // Scoring calculations
  const getPartAScore = () => {
    return partAQuestions.reduce((score, q) => {
      const val = answers[q.id];
      if (val !== undefined && q.isPositive(val)) {
        return score + 1;
      }
      return score;
    }, 0);
  };

  const getPartBScore = () => {
    return partBQuestions.reduce((score, q) => {
      const val = answers[q.id];
      if (val !== undefined && q.isPositive(val)) {
        return score + 1;
      }
      return score;
    }, 0);
  };

  const isPartAComplete = () => {
    return partAQuestions.every(q => answers[q.id] !== undefined);
  };

  const isPartBComplete = () => {
    return partBQuestions.every(q => answers[q.id] !== undefined);
  };

  const isAllComplete = () => {
    return questions.every(q => answers[q.id] !== undefined);
  };

  const resetTest = () => {
    setAnswers({});
    setCurrentStep(0);
    setShowAllQuestions(false);
  };

  const handlePrint = () => {
    window.print();
  };

  // Dynamic text based on language
  const content = {
    da: {
      title: 'ASRS-v1.1 ADHD Screening',
      subtitle: 'Officiel WHO selvtest til voksne',
      introTitle: 'Er det ADHD? Tag en vejledende screening',
      introDesc: 'Adult ADHD Self-Report Scale (ASRS-v1.1) er et anerkendt screeningsværktøj udviklet af Verdenssundhedsorganisationen (WHO) i samarbejde med en række eksperter. Testen kan hjælpe dig med at afklare, om dine symptomer og udfordringer i hverdagen minder om voksen-ADHD.',
      privacyTitle: '100% anonymt og sikkert',
      privacyDesc: 'Du skal ikke opgive navn, e-mail eller andre personlige oplysninger. Dine svar behandles udelukkende lokalt i din browser og gemmes ikke på vores servere.',
      disclaimerTitle: 'Vigtig information',
      disclaimerDesc: 'Denne screening er udelukkende vejledende og kan ikke erstatte en professionel psykiatrisk udredning eller diagnose. Kun en psykiater eller klinisk psykolog kan stille en ADHD-diagnose.',
      startBtn: 'Start screeningen',
      viewAllBtn: 'Vis alle spørgsmål på én side',
      viewStepsBtn: 'Vis trin-for-trin',
      partATitle: 'Del A — Primære indikatorer',
      partADesc: 'Disse 6 spørgsmål er de mest afgørende for at vurdere sandsynligheden for ADHD. Svar så ærligt som muligt ud fra, hvordan du har haft det de seneste 6 måneder.',
      partBTitle: 'Del B — Uddybende symptomer',
      partBDesc: 'Disse 12 spørgsmål giver yderligere klinisk dybde og kortlægger dine symptomer mere detaljeret.',
      nextBtn: 'Næste trin',
      backBtn: 'Tilbage',
      seeResultsBtn: 'Se dit resultat',
      resultsTitle: 'Dit screeningsresultat',
      partAScoreLabel: 'Score i Del A (Primære indikatorer)',
      partBScoreLabel: 'Score i Del B (Uddybende symptomer)',
      positiveIndicators: 'fremtrædende symptomer',
      highProbTitle: 'Resultat indikerer øget sandsynlighed for ADHD',
      highProbDesc: 'Du har svaret bekræftende på 4 eller flere symptomer i Del A. Ifølge WHO\'s scoringsvejledning indikerer dette en stærk sandsynlighed for voksen-ADHD. Vi anbefaler, at du overvejer en klinisk udredning for at få en fuldstændig afklaring.',
      lowProbTitle: 'Resultat indikerer lav sandsynlighed for ADHD',
      lowProbDesc: 'Du har svaret bekræftende på færre end 4 symptomer i Del A. Dette indikerer umiddelbart ikke en øget sandsynlighed for ADHD ud fra de primære indikatorer. Hvis du alligevel oplever udtalte udfordringer i din hverdag, kan det være relevant at tale med en professionel om andre mulige årsager.',
      nextStepsTitle: 'Hvad er næste skridt?',
      bookCta: 'Kontakt os om booking',
      bookCtaDesc: 'Tal med en af vores specialister og få en professionel vurdering af din situation.',
      readAdhdCta: 'Læs mere om ADHD-udredning',
      readAdhdCtaDesc: 'Læs om, hvordan et professionelt udredningsforløb foregår hos os.',
      visitVidensbank: 'Gå til Vidensbanken',
      visitVidensbankDesc: 'Find dybdegående artikler og viden om ADHD og mental sundhed.',
      printBtn: 'Print eller gem som PDF',
      retakeBtn: 'Tag testen igen',
      progressLabel: 'Spørgsmål besvaret',
      mustAnswerAll: 'Besvar venligst alle spørgsmål for at fortsætte.'
    },
    en: {
      title: 'ASRS-v1.1 ADHD Screening',
      subtitle: 'Official WHO self-test for adults',
      introTitle: 'Could it be ADHD? Take an indicative screening',
      introDesc: 'The Adult ADHD Self-Report Scale (ASRS-v1.1) is a recognised screening tool developed by the World Health Organization (WHO) in collaboration with leading experts. The test can help clarify whether your symptoms and challenges in daily life resemble adult ADHD.',
      privacyTitle: '100% anonymous and secure',
      privacyDesc: 'You do not need to provide your name, email or any personal information. Your answers are processed solely in your browser and are never saved on our servers.',
      disclaimerTitle: 'Important information',
      disclaimerDesc: 'This screening is indicative only and cannot replace a professional psychiatric assessment or diagnosis. Only a psychiatrist or clinical psychologist can diagnose ADHD.',
      startBtn: 'Start screening',
      viewAllBtn: 'View all questions on one page',
      viewStepsBtn: 'View step-by-step',
      partATitle: 'Part A — Primary indicators',
      partADesc: 'These 6 questions are the most crucial in assessing the likelihood of ADHD. Please answer as honestly as possible based on how you have felt over the past 6 months.',
      partBTitle: 'Part B — In-depth symptoms',
      partBDesc: 'These 12 questions provide additional clinical depth and map your symptoms in greater detail.',
      nextBtn: 'Next step',
      backBtn: 'Back',
      seeResultsBtn: 'See your result',
      resultsTitle: 'Your screening result',
      partAScoreLabel: 'Score in Part A (Primary indicators)',
      partBScoreLabel: 'Score in Part B (In-depth symptoms)',
      positiveIndicators: 'prominent symptoms',
      highProbTitle: 'Result indicates an increased likelihood of ADHD',
      highProbDesc: "You answered affirmatively to 4 or more symptoms in Part A. According to the WHO scoring guidelines, this indicates a strong likelihood of adult ADHD. We recommend considering a clinical assessment for full clarity.",
      lowProbTitle: 'Result indicates a low likelihood of ADHD',
      lowProbDesc: 'You answered affirmatively to fewer than 4 symptoms in Part A. This does not immediately suggest an increased likelihood of ADHD based on the primary indicators. If you still experience marked challenges in daily life, discussing other potential causes with a professional may be beneficial.',
      nextStepsTitle: 'What are the next steps?',
      bookCta: 'Contact us about booking',
      bookCtaDesc: 'Speak with one of our specialists to receive a professional evaluation of your situation.',
      readAdhdCta: 'Read more about ADHD assessment',
      readAdhdCtaDesc: 'Learn how a professional assessment process is carried out at our clinic.',
      visitVidensbank: 'Go to knowledge base',
      visitVidensbankDesc: 'Find in-depth articles and insights about ADHD and mental health.',
      printBtn: 'Print or save as PDF',
      retakeBtn: 'Retake test',
      progressLabel: 'Questions answered',
      mustAnswerAll: 'Please answer all questions to proceed.'
    }
  };

  const tText = content[lang] || (lang === 'en' ? content.en : content.da);

  // Render Intro Step
  if (currentStep === 0) {
    return (
      <div className="bg-sand-light min-h-screen py-12 md:py-20">
        <div className="max-w-3xl mx-auto px-6">
          {/* Breadcrumb */}
          <div className="mb-8">
            <Link to={localizePath('/vidensbank')} className="inline-flex items-center gap-2 text-sm font-medium text-text-dark/60 hover:text-petrol-medium transition-colors">
              <ChevronLeft className="w-4 h-4" />
              {lang === 'da' ? 'Tilbage til Vidensbanken' : 'Back to knowledge base'}
            </Link>
          </div>

          {/* Header */}
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-petrol-medium/10 text-petrol-dark uppercase tracking-wider mb-4">
              <i className="fa-solid fa-clipboard-question mr-1" /> {tText.subtitle}
            </span>
            <h1 className="font-display text-4xl sm:text-5xl font-normal text-petrol-dark tracking-tight mb-4 leading-tight">
              {tText.introTitle}
            </h1>
            <p className="text-base sm:text-lg text-text-dark/80 leading-relaxed max-w-2xl mx-auto font-serif italic">
              {tText.introDesc}
            </p>
          </div>

          {/* Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <div className="border border-sand-medium/40 bg-sand-light/10 rounded-xl p-6 flex gap-4">
              <div className="w-10 h-10 rounded-lg bg-petrol-medium/10 text-petrol-dark flex items-center justify-center shrink-0">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-display text-base font-bold text-petrol-dark mb-1">{tText.privacyTitle}</h3>
                <p className="text-sm text-text-dark/70 leading-relaxed">{tText.privacyDesc}</p>
              </div>
            </div>

            <div className="border border-sand-medium/40 bg-sand-light/10 rounded-xl p-6 flex gap-4">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 text-amber-700 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-display text-base font-bold text-amber-800 mb-1">{tText.disclaimerTitle}</h3>
                <p className="text-sm text-text-dark/70 leading-relaxed">{tText.disclaimerDesc}</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={() => setCurrentStep(1)}
              className="w-full sm:w-auto px-8 py-4 bg-petrol-dark text-white rounded-full font-semibold hover:bg-petrol-medium transition-all duration-300 shadow-sm hover:shadow-md flex items-center justify-center gap-2 group cursor-pointer"
            >
              {tText.startBtn}
              <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => {
                setShowAllQuestions(true);
                setCurrentStep(1);
              }}
              className="w-full sm:w-auto px-6 py-4 border border-sand-medium/60 text-text-dark hover:bg-sand-light/40 rounded-full font-semibold transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer"
            >
              {tText.viewAllBtn}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Calculate progress
  const totalQuestionsCount = questions.length;
  const answeredCount = questions.filter(q => answers[q.id] !== undefined).length;
  const progressPercent = Math.round((answeredCount / totalQuestionsCount) * 100);

  // Render Questions (Step-by-step or All-in-one)
  const renderQuestionRow = (q, indexGlobal) => {
    const selectedVal = answers[q.id];
    return (
      <div key={q.id} className="border-b border-sand-medium/30 py-6 last:border-b-0">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-center">
          {/* Question Text */}
          <div>
            <span className="text-xs font-bold text-petrol-medium uppercase tracking-wider block mb-1">
              {lang === 'da' ? `Spørgsmål ${indexGlobal + 1} af ${totalQuestionsCount}` : `Fråga ${indexGlobal + 1} av ${totalQuestionsCount}`}
              <span className="ml-2 text-text-dark/40 font-normal">({q.part === 'A' ? 'Del A' : 'Del B'})</span>
            </span>
            <p className="font-display text-base sm:text-lg font-medium text-petrol-dark leading-snug">
              {q.text[lang]}
            </p>
          </div>

          {/* Options Grid */}
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {options.map((opt) => {
              const isSelected = selectedVal === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => handleSelect(q.id, opt.value)}
                  className={`px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium border transition-all duration-200 cursor-pointer ${
                    isSelected
                      ? 'bg-petrol-dark border-petrol-dark text-white shadow-sm'
                      : 'bg-sand-light border-sand-medium/60 text-text-dark/80 hover:bg-sand-light/40 hover:border-sand-medium'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  if (currentStep === 1 || currentStep === 2) {
    const activeQuestions = showAllQuestions 
      ? questions 
      : (currentStep === 1 ? partAQuestions : partBQuestions);

    const isStepComplete = showAllQuestions 
      ? isAllComplete() 
      : (currentStep === 1 ? isPartAComplete() : isPartBComplete());

    return (
      <div className="bg-sand-light min-h-screen py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-8">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-petrol-medium/10 text-petrol-dark uppercase tracking-wider mb-3">
              <i className="fa-solid fa-clipboard-question mr-1" /> {tText.subtitle}
            </span>
            <h1 className="font-display text-3xl sm:text-4xl font-normal text-petrol-dark tracking-tight mb-2">
              {tText.introTitle}
            </h1>
          </div>

          {/* Progress Bar */}
          <div className="bg-sand-light border border-sand-medium/40 rounded-xl p-4 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex-grow">
              <div className="flex justify-between text-xs font-bold text-petrol-dark uppercase tracking-wider mb-1.5">
                <span>{tText.progressLabel}</span>
                <span>{answeredCount} / {totalQuestionsCount} ({progressPercent}%)</span>
              </div>
              <div className="w-full h-2 bg-sand-medium/30 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-petrol-medium transition-all duration-300 rounded-full" 
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
            <button
              onClick={() => {
                setShowAllQuestions(!showAllQuestions);
                setCurrentStep(1);
              }}
              className="text-xs font-bold text-petrol-dark hover:text-petrol-medium transition-colors uppercase tracking-wider shrink-0 cursor-pointer"
            >
              {showAllQuestions ? tText.viewStepsBtn : tText.viewAllBtn}
            </button>
          </div>

          {/* Questions Card */}
          <div className="bg-sand-light border border-sand-medium/40 rounded-2xl p-6 sm:p-8 shadow-sm mb-8">
            <div className="mb-6 pb-4 border-b border-sand-medium/40">
              <h2 className="font-display text-2xl font-normal text-petrol-dark mb-1">
                {showAllQuestions 
                  ? (lang === 'da' ? 'Hele spørgeskemaet' : 'Hela frågeformuläret')
                  : (currentStep === 1 ? tText.partATitle : tText.partBTitle)
                }
              </h2>
              <p className="text-sm text-text-dark/70">
                {showAllQuestions 
                  ? (lang === 'da' ? 'Besvar alle 18 spørgsmål for at se din samlede screening.' : 'Besvara alla 18 frågor för att se din sammanställning.')
                  : (currentStep === 1 ? tText.partADesc : tText.partBDesc)
                }
              </p>
            </div>

            <div className="divide-y divide-sand-medium/30">
              {activeQuestions.map((q, idx) => {
                const globalIndex = showAllQuestions 
                  ? idx 
                  : (currentStep === 1 ? idx : idx + 6);
                return renderQuestionRow(q, globalIndex);
              })}
            </div>
          </div>

          {/* Navigation Controls */}
          <div className="flex justify-between items-center">
            <button
              onClick={() => {
                if (showAllQuestions) {
                  setCurrentStep(0);
                } else if (currentStep === 1) {
                  setCurrentStep(0);
                } else {
                  setCurrentStep(1);
                }
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="px-5 py-3 border border-sand-medium/60 text-text-dark hover:bg-sand-light/40 rounded-full font-semibold transition-all flex items-center gap-2 cursor-pointer"
            >
              <ChevronLeft className="w-4 h-4" />
              {tText.backBtn}
            </button>

            {!isStepComplete && (
              <span className="text-xs text-amber-700 font-medium bg-amber-500/10 px-3 py-1.5 rounded-lg">
                {tText.mustAnswerAll}
              </span>
            )}

            <button
              onClick={() => {
                if (!isStepComplete) return;
                if (showAllQuestions) {
                  setCurrentStep(3);
                } else if (currentStep === 1) {
                  setCurrentStep(2);
                } else {
                  setCurrentStep(3);
                }
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              disabled={!isStepComplete}
              className="px-6 py-3 bg-petrol-dark text-white rounded-full font-semibold hover:bg-petrol-medium disabled:opacity-40 disabled:hover:bg-petrol-dark transition-all flex items-center gap-2 cursor-pointer"
            >
              {showAllQuestions || currentStep === 2 ? tText.seeResultsBtn : tText.nextBtn}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render Results Step
  if (currentStep === 3) {
    const partAScore = getPartAScore();
    const partBScore = getPartBScore();
    const isHighProbability = partAScore >= 4;

    return (
      <div className="bg-sand-light min-h-screen py-12 md:py-20 print:py-0">
        <div className="max-w-4xl mx-auto px-6">
          
          {/* Print Header (Only visible when printing) */}
          <div className="hidden print:block mb-8 border-b border-sand-medium pb-6">
            <div className="flex justify-between items-center">
              <div>
                <div className="font-display text-2xl font-bold text-petrol-dark">Nordisk Psykiatri</div>
                <p className="text-xs text-text-dark/60">Vejledende ADHD-screening (ASRS-v1.1)</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-text-dark/60">Date: {new Date().toLocaleDateString(lang === 'da' ? 'da-DK' : 'en-GB')}</p>
                <p className="text-xs text-text-dark/60">Anonym besvarelse</p>
              </div>
            </div>
          </div>

          {/* Breadcrumb (Hidden on print) */}
          <div className="mb-8 print:hidden">
            <button
              onClick={resetTest}
              className="inline-flex items-center gap-2 text-sm font-medium text-text-dark/60 hover:text-petrol-medium transition-colors cursor-pointer"
            >
              <ChevronLeft className="w-4 h-4" />
              {lang === 'da' ? 'Start forfra' : 'Börja om'}
            </button>
          </div>

          {/* Results Header */}
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-petrol-medium/10 text-petrol-dark uppercase tracking-wider mb-4 print:hidden">
              <CheckCircle2 className="w-3.5 h-3.5" /> {tText.resultsTitle}
            </span>
            <h1 className="font-display text-3xl sm:text-4xl font-normal text-petrol-dark tracking-tight mb-2">
              {tText.resultsTitle}
            </h1>
            <p className="text-sm text-text-dark/60 max-w-lg mx-auto">
              {lang === 'da' 
                ? 'WHO Adult ADHD Self-Report Scale (ASRS-v1.1) scoringsvejledning' 
                : 'WHO Adult ADHD Self-Report Scale (ASRS-v1.1) poängsättning'}
            </p>
          </div>

          {/* Score Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Part A Score */}
            <div className="border border-sand-medium/40 bg-sand-light/10 rounded-xl p-6 text-center flex flex-col justify-between">
              <div>
                <h3 className="font-display text-sm font-bold text-petrol-dark uppercase tracking-wider mb-4">
                  {tText.partAScoreLabel}
                </h3>
                <div className="inline-flex items-baseline gap-1 mb-4">
                  <span className={`text-6xl font-display font-bold ${isHighProbability ? 'text-amber-700' : 'text-petrol-dark'}`}>
                    {partAScore}
                  </span>
                  <span className="text-xl text-text-dark/40">/ 6</span>
                </div>
              </div>
              <p className="text-xs text-text-dark/60 italic">
                {lang === 'da' 
                  ? '4 eller flere positive indikatorer i Del A indikerer øget sandsynlighed for ADHD.' 
                  : '4 eller fler positiva indikatorer i Del A indikerer ökad sannolikhet för ADHD.'}
              </p>
            </div>

            {/* Part B Score */}
            <div className="border border-sand-medium/40 bg-sand-light/10 rounded-xl p-6 text-center flex flex-col justify-between">
              <div>
                <h3 className="font-display text-sm font-bold text-petrol-dark uppercase tracking-wider mb-4">
                  {tText.partBScoreLabel}
                </h3>
                <div className="inline-flex items-baseline gap-1 mb-4">
                  <span className="text-6xl font-display font-bold text-text-dark/80">
                    {partBScore}
                  </span>
                  <span className="text-xl text-text-dark/40">/ 12</span>
                </div>
              </div>
              <p className="text-xs text-text-dark/60 italic">
                {lang === 'da' 
                  ? 'Del B kortlægger hyppigheden af yderligere symptomer til klinisk brug.' 
                  : 'Del B kartlägger frekvensen av ytterligare symptom för klinisk användning.'}
              </p>
            </div>
          </div>

          {/* Detailed Assessment Box */}
          <div className={`border rounded-2xl p-6 sm:p-8 mb-12 ${
            isHighProbability 
              ? 'border-amber-500/30 bg-amber-500/5 text-amber-900' 
              : 'border-petrol-medium/20 bg-petrol-medium/5 text-petrol-dark'
          }`}>
            <div className="flex gap-4 items-start">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                isHighProbability ? 'bg-amber-500/15 text-amber-700' : 'bg-petrol-medium/15 text-petrol-dark'
              }`}>
                {isHighProbability ? <AlertTriangle className="w-6 h-6" /> : <CheckCircle2 className="w-6 h-6" />}
              </div>
              <div>
                <h2 className="font-display text-xl font-bold mb-3">
                  {isHighProbability ? tText.highProbTitle : tText.lowProbTitle}
                </h2>
                <p className="text-sm sm:text-base text-text-dark/80 leading-relaxed mb-4">
                  {isHighProbability ? tText.highProbDesc : tText.lowProbDesc}
                </p>
                <div className="border-t border-current/10 pt-4 mt-4 text-xs text-text-dark/60 italic">
                  <strong>{tText.disclaimerTitle}:</strong> {tText.disclaimerDesc}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons (Hidden on print) */}
          <div className="flex flex-wrap gap-4 justify-center mb-16 print:hidden">
            <button
              onClick={handlePrint}
              className="px-6 py-3 border border-sand-medium/60 text-text-dark hover:bg-sand-light/40 rounded-full font-semibold transition-all flex items-center gap-2 cursor-pointer"
            >
              <Printer className="w-4 h-4" />
              {tText.printBtn}
            </button>
            <button
              onClick={resetTest}
              className="px-6 py-3 border border-sand-medium/60 text-text-dark hover:bg-sand-light/40 rounded-full font-semibold transition-all flex items-center gap-2 cursor-pointer"
            >
              <RotateCcw className="w-4 h-4" />
              {tText.retakeBtn}
            </button>
          </div>

          {/* Next Steps (Hidden on print) */}
          <div className="print:hidden">
            <h3 className="font-display text-2xl font-normal text-petrol-dark text-center mb-8">
              {tText.nextStepsTitle}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Book Cta */}
              <Link 
                to={localizePath('/kontakt')}
                className="border border-sand-medium/40 bg-sand-light rounded-xl p-6 hover:border-petrol-medium/40 hover:shadow-md transition-all duration-300 group flex flex-col justify-between"
              >
                <div>
                  <div className="w-10 h-10 rounded-lg bg-petrol-medium/10 text-petrol-dark flex items-center justify-center mb-4 group-hover:bg-petrol-medium group-hover:text-white transition-colors">
                    <i className="fa-solid fa-calendar-check text-base" />
                  </div>
                  <h4 className="font-display text-base font-bold text-petrol-dark mb-2">
                    {tText.bookCta}
                  </h4>
                  <p className="text-xs text-text-dark/70 leading-relaxed mb-4">
                    {tText.bookCtaDesc}
                  </p>
                </div>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-petrol-dark group-hover:text-petrol-medium transition-colors uppercase tracking-wider">
                  {lang === 'da' ? 'Gå til kontakt' : 'Go to contact'}
                  <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>

              {/* Read ADHD Cta */}
              <Link 
                to={localizePath('/adhd')}
                className="border border-sand-medium/40 bg-sand-light rounded-xl p-6 hover:border-petrol-medium/40 hover:shadow-md transition-all duration-300 group flex flex-col justify-between"
              >
                <div>
                  <div className="w-10 h-10 rounded-lg bg-petrol-medium/10 text-petrol-dark flex items-center justify-center mb-4 group-hover:bg-petrol-medium group-hover:text-white transition-colors">
                    <i className="fa-solid fa-brain text-base" />
                  </div>
                  <h4 className="font-display text-base font-bold text-petrol-dark mb-2">
                    {tText.readAdhdCta}
                  </h4>
                  <p className="text-xs text-text-dark/70 leading-relaxed mb-4">
                    {tText.readAdhdCtaDesc}
                  </p>
                </div>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-petrol-dark group-hover:text-petrol-medium transition-colors uppercase tracking-wider">
                  {lang === 'da' ? 'Læs mere' : 'Read more'}
                  <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>

              {/* Vidensbank Cta */}
              <Link 
                to={localizePath('/vidensbank')}
                className="border border-sand-medium/40 bg-sand-light rounded-xl p-6 hover:border-petrol-medium/40 hover:shadow-md transition-all duration-300 group flex flex-col justify-between"
              >
                <div>
                  <div className="w-10 h-10 rounded-lg bg-petrol-medium/10 text-petrol-dark flex items-center justify-center mb-4 group-hover:bg-petrol-medium group-hover:text-white transition-colors">
                    <i className="fa-solid fa-book-open text-base" />
                  </div>
                  <h4 className="font-display text-base font-bold text-petrol-dark mb-2">
                    {tText.visitVidensbank}
                  </h4>
                  <p className="text-xs text-text-dark/70 leading-relaxed mb-4">
                    {tText.visitVidensbankDesc}
                  </p>
                </div>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-petrol-dark group-hover:text-petrol-medium transition-colors uppercase tracking-wider">
                  {lang === 'da' ? 'Gå til vidensbank' : 'Go to knowledge base'}
                  <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </div>
          </div>

          {/* Print-only detailed answers list */}
          <div className="hidden print:block mt-12 border-t border-sand-medium pt-8">
            <h3 className="font-display text-lg font-bold text-petrol-dark mb-6">Dine besvarelser</h3>
            <div className="space-y-4">
              {questions.map((q, idx) => {
                const ansVal = answers[q.id];
                const optLabel = options.find(o => o.value === ansVal)?.label || '-';
                const isPos = ansVal !== undefined && q.isPositive(ansVal);
                return (
                  <div key={q.id} className="flex justify-between items-start text-xs border-b border-sand-medium/20 pb-2">
                    <div className="max-w-[75%]">
                      <span className="font-bold text-text-dark/60 mr-2">{idx + 1}.</span>
                      <span className="text-text-dark/80">{q.text[lang]}</span>
                    </div>
                    <div className="text-right shrink-0">
                      <span className={`font-semibold ${isPos ? 'text-amber-700' : 'text-text-dark/80'}`}>
                        {optLabel}
                      </span>
                      {isPos && <span className="ml-1 text-[10px] text-amber-600 font-bold">(Symptom)</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      </div>
    );
  }

  return null;
}
